"use client";

import * as React from "react";
import { ActionButtons } from "~/components/tool-ui/shared/action-buttons";
import { type Action } from "~/components/tool-ui/shared/schema";
import { cn, Separator, Tooltip, TooltipContent, TooltipTrigger } from "./_adapter";
import type {
  ApprovalCardProps,
  ApprovalDecision,
  ApprovalSection,
  ApprovalSectionItem,
} from "./schema";

import { icons, Check, X } from "lucide-react";

type LucideIcon = React.ComponentType<{ className?: string }>;

// Field-heavy cards (e.g. an ingest preview with many profiled columns) would
// otherwise render as one long scroll. When `collapsibleSections` is set the
// rows are capped to this many and the remainder revealed by a toggle.
export const COLLAPSED_SECTION_ITEM_LIMIT = 8;

// Truncate `sections` to at most `limit` total items, preserving section order
// and grouping: each section keeps however many of its items fit in the
// remaining budget, and once the budget is spent later sections are dropped.
// Returns the capped sections and how many items were hidden.
export function capApprovalSections(
  sections: readonly ApprovalSection[],
  limit: number
): { sections: ApprovalSection[]; hiddenCount: number } {
  let budget = limit;
  const capped: ApprovalSection[] = [];
  for (const section of sections) {
    if (budget <= 0) break;
    const items = section.items.slice(0, budget);
    budget -= items.length;
    capped.push({ ...section, items });
  }
  const total = sections.reduce((count, section) => count + section.items.length, 0);
  return { sections: capped, hiddenCount: Math.max(0, total - limit) };
}

function getLucideIcon(name: string): LucideIcon | null {
  const pascalName = name
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");

  const Icon = icons[pascalName as keyof typeof icons];
  return Icon ?? null;
}

interface ApprovalCardReceiptProps {
  id: string;
  title: string;
  choice: ApprovalDecision;
  actionLabel?: string;
  className?: string;
}

function ApprovalCardReceipt({
  id,
  title,
  choice,
  actionLabel,
  className,
}: ApprovalCardReceiptProps) {
  const isApproved = choice === "approved";
  const displayLabel = actionLabel ?? (isApproved ? "Approved" : "Denied");

  return (
    <div
      className={cn(
        "flex w-full min-w-64 max-w-2xl flex-col",
        "text-foreground",
        "motion-safe:animate-in motion-safe:fade-in motion-safe:blur-in-sm motion-safe:zoom-in-95 motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.16,1,0.3,1)] motion-safe:fill-mode-both",
        className
      )}
      data-slot="approval-card"
      data-tool-ui-id={id}
      data-receipt="true"
      role="status"
      aria-label={displayLabel}
    >
      <div
        className={cn(
          "bg-card/60 flex w-full items-center gap-3 rounded-2xl border px-4 py-3 shadow-xs"
        )}
      >
        <span
          className={cn(
            "flex size-8 shrink-0 items-center justify-center rounded-full bg-muted",
            isApproved ? "text-primary" : "text-muted-foreground"
          )}
        >
          {isApproved ? <Check className="size-4" /> : <X className="size-4" />}
        </span>
        <div className="flex flex-col">
          <span className="text-sm font-medium">{displayLabel}</span>
          <span className="text-muted-foreground text-sm">{title}</span>
        </div>
      </div>
    </div>
  );
}

// Default column headers for a section table when the card supplies none. A
// general `ask_for_approval` section has no inherent column names, so the item
// fields map to these; a card that knows its columns (the ingest preview)
// overrides them via `sectionTableHeaders`.
const DEFAULT_SECTION_TABLE_HEADERS = {
  label: "Field",
  value: "Value",
  description: "Description",
} as const;

interface SectionColumn {
  key: "label" | "value" | "description";
  header: string;
  render: (item: ApprovalSectionItem) => React.ReactNode;
  muted: boolean;
  wrap: boolean;
  // Render cells in this column monospace. The field-name column holds code
  // identifiers (e.g. PERSON_ID), which read as inconsistent in the proportional
  // UI font; a fixed-width font keeps them uniform.
  mono: boolean;
}

// Which optional columns to show, resolved once across every section's items so
// the layout is uniform: the value/description column appears if *any* item in
// the card supplies that field. A label-only card renders one column, a
// label+value card two — never an empty column. Headers default to generic
// labels; `sectionTableHeaders` overrides them (e.g. Column / Type / Description).
function resolveSectionColumns(
  sections: readonly ApprovalSection[],
  headers?: ApprovalCardProps["sectionTableHeaders"]
): SectionColumn[] {
  const resolvedHeaders = headers ?? DEFAULT_SECTION_TABLE_HEADERS;
  const items = sections.flatMap((section) => section.items);
  const columns: SectionColumn[] = [
    {
      key: "label",
      header: resolvedHeaders.label,
      render: (item) => item.label,
      muted: false,
      wrap: false,
      mono: true,
    },
  ];
  if (items.some((item) => item.value)) {
    columns.push({
      key: "value",
      header: resolvedHeaders.value,
      render: (item) => item.value,
      muted: true,
      wrap: false,
      mono: false,
    });
  }
  if (items.some((item) => item.description)) {
    columns.push({
      key: "description",
      header: resolvedHeaders.description,
      render: (item) => item.description,
      muted: true,
      wrap: true,
      mono: false,
    });
  }
  return columns;
}

interface ApprovalSectionsTableProps {
  sections: readonly ApprovalSection[];
  headers?: ApprovalCardProps["sectionTableHeaders"];
}

// Renders every section in one table so their columns line up: a single table
// shares one set of column widths, so the label column sizes to the widest label
// in the whole card and the value/description columns start at the same x in
// every section (separate per-section tables each sized independently, leaving
// the value column ragged across sections). Each section contributes a heading
// row spanning all columns, its own Field / Value header row, then its items.
function ApprovalSectionsTable({ sections, headers }: ApprovalSectionsTableProps) {
  const columns = resolveSectionColumns(sections, headers);

  return (
    <table className="w-full text-sm">
      {sections.map((section, sectionIndex) => (
        <tbody key={sectionIndex}>
          <tr>
            <td
              colSpan={columns.length}
              className={cn("pb-1 align-bottom", sectionIndex > 0 && "pt-4")}
            >
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-medium leading-tight">{section.heading}</h3>
                {section.badge && (
                  <span className="bg-primary/10 text-primary shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                    {section.badge}
                  </span>
                )}
              </div>
            </td>
          </tr>
          {section.items.length > 0 && (
            <>
              <tr className="text-muted-foreground text-left text-xs">
                {columns.map((column, index) => (
                  <th
                    key={column.key}
                    scope="col"
                    className={cn(
                      "py-1 font-medium",
                      index === columns.length - 1 ? "w-full" : "pr-3 whitespace-nowrap"
                    )}
                  >
                    {column.header}
                  </th>
                ))}
              </tr>
              {section.items.map((item, itemIndex) => (
                <tr key={itemIndex} className="border-t align-top">
                  {columns.map((column, index) => (
                    <td
                      key={column.key}
                      className={cn(
                        "py-1.5 text-xs",
                        column.mono && "font-mono",
                        column.muted && "text-muted-foreground",
                        column.wrap ? "leading-snug" : "whitespace-nowrap",
                        index < columns.length - 1 && "pr-3"
                      )}
                    >
                      {column.render(item)}
                    </td>
                  ))}
                </tr>
              ))}
            </>
          )}
        </tbody>
      ))}
    </table>
  );
}

// Render a metadata value, drawing backtick-fenced identifiers (e.g. `PERSON_ID`)
// in a monospace font so field names read consistently with the section table's
// field column. Unfenced prose renders unchanged; an unmatched backtick is left
// literal.
function renderMetadataValue(value: string): React.ReactNode {
  return value.split(/`([^`]+)`/g).map((part, index) =>
    index % 2 === 1 ? (
      <code key={index} className="font-mono">
        {part}
      </code>
    ) : (
      part
    )
  );
}

// A metadata row's value: truncated to a single line, revealing the full text in
// a tooltip on hover only when it actually overflows. The tooltip renders the
// same backtick-aware content, so field names stay monospace there too.
function MetadataValue({ value }: { value: string }) {
  const ref = React.useRef<HTMLElement>(null);
  const [open, setOpen] = React.useState(false);
  const content = renderMetadataValue(value);

  return (
    <Tooltip
      delayDuration={200}
      open={open}
      onOpenChange={(next) => {
        const el = ref.current;
        setOpen(next && !!el && el.scrollWidth > el.clientWidth);
      }}
    >
      <TooltipTrigger asChild>
        <dd ref={ref} className="min-w-0 truncate">
          {content}
        </dd>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{content}</TooltipContent>
    </Tooltip>
  );
}

export function ApprovalCard({
  id,
  title,
  description,
  icon,
  metadata,
  sections,
  variant,
  confirmLabel,
  cancelLabel,
  className,
  choice,
  onConfirm,
  onCancel,
  collapsibleSections,
  sectionTableHeaders,
}: ApprovalCardProps) {
  const resolvedVariant = variant ?? "default";
  const resolvedConfirmLabel = confirmLabel ?? "Approve";
  const resolvedCancelLabel = cancelLabel ?? "Deny";
  const Icon = icon ? getLucideIcon(icon) : null;

  const [sectionsExpanded, setSectionsExpanded] = React.useState(false);
  const totalSectionItems =
    sections?.reduce((count, section) => count + section.items.length, 0) ?? 0;
  const canCollapseSections =
    !!collapsibleSections && totalSectionItems > COLLAPSED_SECTION_ITEM_LIMIT;
  const visibleSections =
    canCollapseSections && !sectionsExpanded
      ? capApprovalSections(sections ?? [], COLLAPSED_SECTION_ITEM_LIMIT).sections
      : (sections ?? []);

  const handleAction = React.useCallback(
    async (actionId: string) => {
      if (actionId === "confirm") {
        await onConfirm?.();
      } else if (actionId === "cancel") {
        await onCancel?.();
      }
    },
    [onConfirm, onCancel]
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel?.();
      }
    },
    [onCancel]
  );

  const isDestructive = resolvedVariant === "destructive";

  const actions: Action[] = [
    {
      id: "cancel",
      label: resolvedCancelLabel,
      variant: "ghost",
    },
    {
      id: "confirm",
      label: resolvedConfirmLabel,
      variant: isDestructive ? "destructive" : "default",
    },
  ];

  const viewKey = choice ? `receipt-${choice}` : "interactive";

  return (
    <div key={viewKey} className="contents">
      {choice ? (
        <ApprovalCardReceipt id={id} title={title} choice={choice} className={className} />
      ) : (
        <article
          className={cn(
            "flex w-full min-w-64 max-w-2xl flex-col gap-3",
            "text-foreground",
            className
          )}
          data-slot="approval-card"
          data-tool-ui-id={id}
          role="dialog"
          aria-labelledby={`${id}-title`}
          aria-describedby={description ? `${id}-description` : undefined}
          onKeyDown={handleKeyDown}
        >
          <div className="bg-card flex w-full flex-col gap-4 rounded-2xl border p-5 shadow-xs">
            <div className="flex items-start gap-3">
              {Icon && (
                <span
                  className={cn(
                    "flex size-10 shrink-0 items-center justify-center rounded-xl",
                    isDestructive
                      ? "bg-destructive/10 text-destructive"
                      : "bg-primary/10 text-primary"
                  )}
                >
                  <Icon className="size-5" />
                </span>
              )}
              <div className="flex flex-1 flex-col gap-1">
                <h2 id={`${id}-title`} className="text-base font-semibold leading-tight">
                  {title}
                </h2>
                {description && (
                  <p id={`${id}-description`} className="text-muted-foreground text-sm">
                    {description}
                  </p>
                )}
              </div>
            </div>

            {metadata && metadata.length > 0 && (
              <>
                <Separator />
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                  {metadata.map((item, index) => (
                    <React.Fragment key={index}>
                      <dt className="text-muted-foreground">{item.key}</dt>
                      <MetadataValue value={item.value} />
                    </React.Fragment>
                  ))}
                </dl>
              </>
            )}

            {sections && sections.length > 0 && (
              <>
                <Separator />
                <div className="flex flex-col gap-3">
                  <ApprovalSectionsTable sections={visibleSections} headers={sectionTableHeaders} />
                  {canCollapseSections && (
                    <button
                      type="button"
                      onClick={() => setSectionsExpanded((expanded) => !expanded)}
                      aria-expanded={sectionsExpanded}
                      className="text-muted-foreground hover:text-foreground cursor-pointer self-start text-xs font-medium"
                    >
                      {sectionsExpanded ? "Show fewer" : `Show all ${totalSectionItems} fields`}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="@container/actions">
            <ActionButtons actions={actions} onAction={handleAction} />
          </div>
        </article>
      )}
    </div>
  );
}
